import { useRef } from "react";

import {
  FieldItemProps,
  FieldItemStyles,
  IStackStyles,
  mergeStyleSets,
  Stack
} from "@bps/fluent-ui";
import { ContactType } from "@libs/gateways/practice/PracticeGateway.dtos.ts";
import {
  ContactPickerField,
  ContactPickerFieldProps
} from "@ui-components/pickers/contact-picker/ContactPickerField.tsx";

import {
  ContactPickerFieldQuickAdd,
  ContactPickerFieldQuickAddProps
} from "./ContactPickerQuickAdd.tsx";

export type ContactPickerFieldWithAddProps = ContactPickerFieldProps & {
  quickAddProps?: ContactPickerFieldQuickAddProps;
  tagInUseKeys?: string[];
  wrapperStyles?: IStackStyles;
  isPatient?: boolean;
  pickerFormItemStyles?: Partial<Pick<FieldItemStyles, "item" | "root">>;
} & Pick<FieldItemProps, "actionButton">;

/**
 * ContactPickerField component with add new contact functionality. It can be used for both Contact and Patient.
 * The default type is Contact. ⚠️ Use filter prop from ContactPickerFieldProps to specify another type in addition to
 * typeToAdd.
 *
 * @param actionButton - optional feature to render action link;
 * @param typeToAdd - type for adding a contact in QuickAddContactDialog, the default type is Individual
 * @param placeholder - field placeholder, the default value is "Search for person"
 * @param tagInUseKeys - array of ids to prevent selections of already selected contacts if the field is an array item
 * @param wrapperStyles - styles for the root element
 * @param autoFocus - set autofocus flag
 * @param pickerFormItemStyles
 * @param contactPickerFieldProps - ContactPickerFieldProps
 */
export const ContactPickerFieldWithAdd: React.FunctionComponent<
  ContactPickerFieldWithAddProps
> = ({
  placeholder,
  wrapperStyles,
  pickerFormItemStyles,
  quickAddProps,
  ...contactPickerFieldProps
}) => {
  const types: (ContactType | string)[] = contactPickerFieldProps.filter
    ?.types ?? [ContactType.Individual];

  const relatedContactText = useRef<string | undefined>();

  return (
    <Stack
      horizontalAlign="space-between"
      horizontal
      verticalAlign="start"
      tokens={{ childrenGap: 8 }}
      styles={mergeStyleSets(wrapperStyles, {
        root: {
          position: "relative",
          paddingBottom: 0,
          marginTop: 0
        }
      })}
    >
      <ContactPickerField
        {...contactPickerFieldProps}
        filter={{ types }}
        onInputChange={(value: string) => {
          if (value) {
            relatedContactText.current = value;
          }
          return value;
        }}
        placeholder={placeholder ?? "Search for person"}
        fieldItemStyle={mergeStyleSets(
          { root: { flex: 1 } },
          pickerFormItemStyles
        )}
      />
      <ContactPickerFieldQuickAdd
        {...quickAddProps}
        {...contactPickerFieldProps}
        relatedContactText={relatedContactText}
      />
    </Stack>
  );
};
